import { useEffect, useState, useCallback } from "react";
import {
  faPlus,
  faGear,
  faVideo,
  faClock,
  faHistory,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import ShortStatCard from "./ShortStatCard";
import Button from "../../common/Button";
import Loading from "../../common/Loading";
import MessageText from "../../common/MessageText";
import Page from "../../common/Page";
import APIManager from "../../../managers/APIManager";
import { useSelector } from "react-redux";
import LanguageSelector from "../../common/LanguageSelector";
import SiteSelector from "../../common/SiteSelector";
import VideoListCard from "./VideoListCard";

const AIVideoHomepage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);

  const [liveVideos, setLiveVideos] = useState([]);
  const [liveVideosTotal, setLiveVideosTotal] = useState(0);
  const [pastVideos, setPastVideos] = useState([]);
  const [pastVideosTotal, setPastVideosTotal] = useState(0);

  const siteVirgulId = useSelector(
    (state) => state.site?.selectedSite?.virgulId
  );
  const videoCreation = useSelector(
    (state) => state.site?.selectedSite?.videoCreation
  );

  const fetchVideos = useCallback(() => {
    if (!videoCreation || !siteVirgulId) {
      setLoading(false);
      navigate("/");
      return;
    }

    setLoading(true);
    APIManager.getAIVideos({
      siteVirgulId,
      page: 1,
      perPage: 4,
      isPublished: false,
    })
      .then((res) => {
        setPastVideos(res?.data || []);
        setPastVideosTotal(res?.totalItems || 0);
        APIManager.getAIVideos({
          siteVirgulId,
          page: 1,
          perPage: 4,
          isPublished: true,
        })
          .then((res) => {
            setLiveVideos(res?.data || []);
            setLiveVideosTotal(res?.totalItems || 0);
          })
          .catch((error) => {
            console.error("Error fetching AI videos:", error);
            setLiveVideos([]);
            setLiveVideosTotal(0);
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch((error) => {
        console.error("Error fetching AI videos:", error);
        setPastVideos([]);
        setPastVideosTotal(0);
        setLiveVideos([]);
        setLiveVideosTotal(0);
        setLoading(false);
      });
  }, [videoCreation, siteVirgulId, navigate]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  if (loading) {
    return <Loading />;
  }

  return (
    <Page
      title={
        <div className="flex-col">
          <div className="sm:flex sm:items-center justify-between">
            <div className="flex items-center">
              <SiteSelector
                siteFilter={(site) =>
                  site.videoCreation && site.id !== "all-sites"
                }
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                className="ml-auto"
                onClick={() => navigate("/ai-video/new")}
                variant="primary"
                icon={faPlus}
              >
                {t("new video")}
              </Button>
              <Button
                className="ml-auto"
                onClick={() => navigate("/ai-video/settings")}
                variant="primary"
                icon={faGear}
              >
                {t("video creation settings")}
              </Button>
              <LanguageSelector />
            </div>
          </div>
          <h2 className="text-xl font-bold text-gray-700 tracking-tight leading-tight my-4">
            {t("video creator")}
          </h2>
          <p className="text-gray-500 text-sm">
            {t("video creator description")}
          </p>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ShortStatCard
          title={t("total videos")}
          value={liveVideosTotal + pastVideosTotal}
          icon={faVideo}
        />
        <ShortStatCard
          title={t("live videos")}
          value={liveVideosTotal}
          icon={faClock}
          isPublished={true}
        />
        <ShortStatCard
          title={t("ready to publish videos")}
          value={pastVideosTotal}
          icon={faHistory}
          isPublished={false}
        />
      </div>
      <div>
        {!liveVideos.length && !pastVideos.length ? (
          <div
            className="flex flex-col items-center justify-center"
            style={{ minHeight: "500px" }}
          >
            <div>
              <MessageText
                message={t(
                  "there are no ai videos available for your account."
                )}
                textAlignment="text-center"
              />
            </div>
            <div className="flex items-center justify-center space-x-2">
              <Button
                className="mt-4"
                onClick={() => navigate("/ai-video/new")}
                variant="primary"
                icon={faPlus}
              >
                {t("new video")}
              </Button>
              <Button
                className="mt-4"
                onClick={() => navigate("/ai-video/settings")}
                variant="primary"
                icon={faGear}
              >
                {t("video creation settings")}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4 gap-4">
              <VideoListCard
                videos={liveVideos}
                title={t("live videos")}
                isPublished={true}
              />
              <VideoListCard
                videos={pastVideos}
                title={t("ready to publish videos")}
                isPublished={false}
              />
            </div>
          </>
        )}
      </div>
    </Page>
  );
};

export default AIVideoHomepage;
